export const safeStringify = (obj: any) => {
  try {
    return JSON.stringify(obj);
  } catch (e) {
    console.warn("JSON.stringify failed, attempting to handle circular references:", e);

    const seen = new WeakSet();
    const circularPaths: string[] = [];
    const pathStack: string[] = [];

    try {
      const result = JSON.stringify(obj, function(key, value) {
        if (key) {
          while (pathStack.length > 0 && this !== obj) {
            pathStack.pop();
          }
          pathStack.push(key);
        }

        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) {
            const path = pathStack.join('.');
            circularPaths.push(path);
            console.warn(`Circular reference detected at path: ${path}`);
            return '[Circular Reference]';
          }
          seen.add(value);
        }
        return value;
      });

      if (circularPaths.length > 0) {
        console.warn(`safeStringify: Removed ${circularPaths.length} circular reference(s)`);
      }

      return result;
    } catch (e2) {
      console.error("safeStringify failed completely:", e2);
      return "{}";
    }
  }
};

export class StorageAdapter {
  getItem<T>(key: string, fallback: T): T {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return fallback;

      try {
        return JSON.parse(stored);
      } catch (parseError) {
        console.error(`CRITICAL: JSON Corruption detected for key ${key}. Resetting to fallback.`, parseError);
        return fallback;
      }
    } catch (e) {
      console.error(`Failed to load ${key}`, e);
      return fallback;
    }
  }

  setItem(key: string, value: any): void {
    try {
      const stringified = safeStringify(value);
      localStorage.setItem(key, stringified);
    } catch (e: any) {
      if (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
        alert("CRITICAL: Storage Quota Exceeded! Your last action (likely an image/video upload) was NOT saved. Please delete some media or clear data to continue.");
        console.error("LocalStorage Quota Exceeded. Data not saved.", e);
      } else {
        console.error(`Failed to save ${key}`, e);
      }
    }
  }

  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error(`Failed to remove ${key}`, e);
    }
  }

  clear(): void {
    try {
      localStorage.clear();
    } catch (e) {
      console.error("Failed to clear storage", e);
    }
  }
}

export const storageAdapter = new StorageAdapter();
